import { BookOpen, Package } from "lucide-react";
import { getThumbUrl } from "@/lib/image-utils";

interface Book {
  id: string;
  title: string;
  description: string | null;
  author: string | null;
  cover_url: string | null;
  type: "physical" | "ebook";
  price: number;
  stock_quantity: number | null;
  is_preorder?: boolean | null;
  compare_at_price?: number | null;
  slug?: string | null;
}

interface BookCardProps {
  book: Book;
  onSelect: (book: Book) => void;
}

const BookCard = ({ book, onSelect }: BookCardProps) => {
  const isOutOfStock = book.type === "physical" && book.stock_quantity !== null && book.stock_quantity <= 0;

  return (
    <div onClick={() => onSelect(book)} className="group cursor-pointer">
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:shadow-xl group-hover:shadow-ink/15 bg-cream-soft flex items-center justify-center p-6">
        {book.cover_url ? (
          <img
            src={getThumbUrl(book.cover_url) || book.cover_url}
            alt={book.title}
            loading="lazy"
            decoding="async"
            width={400}
            height={600}
            onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
            className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex items-center justify-center">
            {book.type === "ebook" ? (
              <BookOpen className="w-14 h-14 text-terracotta" />
            ) : (
              <Package className="w-14 h-14 text-terracotta" />
            )}
          </div>
        )}

        {book.is_preorder && (
          <span className="absolute top-3 left-3 text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-terracotta text-cream shadow-md">
            Pre-order
          </span>
        )}

        {!book.is_preorder && book.type !== "physical" && (
          <span className="absolute top-3 left-3 text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-cream/90 text-ink backdrop-blur-sm">
            {book.type === "ebook" ? "E. knyga" : "Audio knyga"}
          </span>
        )}

        {isOutOfStock && (
          <span className="absolute top-3 right-3 text-[10px] px-2.5 py-1 rounded-full font-semibold bg-destructive/90 text-destructive-foreground">
            Nėra sandėlyje
          </span>
        )}
      </div>

      <div className="px-1 pt-3 pb-1">
        <h3 className="font-marquee font-bold text-sm md:text-base leading-tight mb-0.5 line-clamp-2 text-ink">
          {book.title}
        </h3>
        {book.author && (
          <p className="text-[11px] text-ink/55 mb-1">{book.author}</p>
        )}
        <p className="text-sm font-bold text-terracotta">
          €{book.price.toFixed(2)}
          {book.compare_at_price && book.compare_at_price > book.price && (
            <span className="ml-2 text-ink/40 font-normal line-through">€{book.compare_at_price.toFixed(2)}</span>
          )}
        </p>
      </div>
    </div>
  );
};

export default BookCard;
export type { Book };
